import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useVisibilityRefresh = () => {
  const queryClient = useQueryClient();
  const lastVisibilityChange = useRef<number>(0);
  const realtimeChannels = useRef<Set<any>>(new Set());

  useEffect(() => {
    const handleVisibilityChange = async () => {
      const now = Date.now();
      
      // Throttle to 5 seconds minimum
      if (now - lastVisibilityChange.current < 5000) return;
      
      // Calculate inactivity duration
      const inactivityDuration = now - lastVisibilityChange.current;
      lastVisibilityChange.current = now;

      if (!document.hidden) {
        // Only refresh if tab was hidden for more than 5 minutes
        if (inactivityDuration > 5 * 60 * 1000) {
          console.log('Tab was hidden for >5min - refreshing assignments only');
          
          // Only refresh assignments list, not individual device details
          // Realtime will handle device updates
          await queryClient.invalidateQueries({ 
            queryKey: ["device-assignments"],
            refetchType: 'all'
          });
        } else if (import.meta.env.DEV) {
          console.log('Tab became visible (short duration) - relying on realtime');
        }
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also listen for focus/blur events as backup
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [queryClient]);

  // Function to track real-time channels
  const trackChannel = (channel: any) => {
    if (channel) {
      realtimeChannels.current.add(channel);
      
      // Clean up when channel is removed
      const originalUnsubscribe = channel.unsubscribe;
      channel.unsubscribe = function() {
        realtimeChannels.current.delete(channel);
        return originalUnsubscribe.call(this);
      };
    }
  };

  return { trackChannel };
};